import useComponentSize from '@rehooks/component-size';
import { first, last, sortBy } from 'lodash';
import moment, { max } from 'moment/moment';
import * as React from 'react';
import { useEffect, useMemo } from 'react';

import {
  CELL_SPACING,
  CELL_WIDTH,
  EmployeeCalendarDate,
  EmployeeCalendarMonth,
  handleCalendarRangeEndDST,
  resolveCalendarRangeCapDate,
} from '~/components/EmployeeCalendar/data';
import { EmployeeCalendarNavigation } from '~/components/EmployeeCalendar/EmployeeCalendarHeader';
import { useHasChanged } from '~/hooks/useHasChanged';

type PropsType = {
  startDate: Date;
  endDate: Date;
  showOutOfRange?: boolean;
};

export const useEmployeeCalendar = (props: PropsType) => {
  const { startDate, endDate, showOutOfRange } = props;

  const containerRef = React.useRef<HTMLTableElement>(null);
  const { width: calendarWidth } = useComponentSize(containerRef);

  const numberOfDaysInView = Math.floor(
    calendarWidth / (CELL_WIDTH + CELL_SPACING * 2),
  );
  const [viewStartDate, setViewStartDate] = React.useState<Date>(startDate);

  const startDateHasChanged = useHasChanged(moment(startDate).format('L'));
  const endDateHasChanged = useHasChanged(moment(endDate).format('L'));
  useEffect(() => {
    if (
      startDateHasChanged ||
      (endDateHasChanged && moment(viewStartDate).isAfter(endDate))
    ) {
      setViewStartDate(startDate);
    }
  }, [
    viewStartDate,
    startDateHasChanged,
    startDate,
    endDateHasChanged,
    endDate,
    setViewStartDate,
  ]);

  const handleCalendarNavigation = (direction: string) => {
    if (direction === 'LEFT') {
      setViewStartDate(
        max(
          moment(startDate),
          moment(viewStartDate).clone().subtract(numberOfDaysInView, 'days'),
        ).toDate(),
      );
    } else {
      setViewStartDate(
        moment(viewStartDate).clone().add(numberOfDaysInView, 'days').toDate(),
      );
    }
  };

  const { daysInView, datesInView, monthsInView } = useMemo(() => {
    // Resolve Dates In View
    const daysInView = Array.from(Array(numberOfDaysInView)).map((_v, index) =>
      moment(viewStartDate).clone().add(index, 'days').toDate(),
    );

    const datesInView = daysInView.map((date): EmployeeCalendarDate => {
      const momentDate = moment(date);

      return {
        id: momentDate.format('YYYY-MM-DD'),
        date: date,
        day: momentDate.format('D'),
        weekday: momentDate.format('dd'),
        inRange: momentDate.isSameOrBefore(moment(endDate)),
        isWeekend: [5, 6].includes(momentDate.weekday()),
        isToday: moment().isSame(momentDate, 'day'),
      };
    });

    const visibleDatesInView = !!showOutOfRange
      ? datesInView
      : datesInView.filter((d) => d.inRange);

    // Resolve Months In View
    const lastInView = last(visibleDatesInView)?.date;
    const getMonthId = (day?: Date): string | undefined =>
      !!day ? moment(day).format('YYYY-MM') : undefined;
    const lastMonthInView = getMonthId(lastInView);

    const monthsInView = daysInView
      .reduce((months, day) => {
        const id = getMonthId(day);

        if (!!id && !months.some((month) => month.id === id)) {
          months.push({
            id,
            label: day.toLocaleString('default', {
              month: 'long',
              year: 'numeric',
            }),
            showOnDate: day,
            daysCount: 1,
          });
        }

        return months;
      }, [] as EmployeeCalendarMonth[])
      .map((month) => {
        const monthEndDate = moment(month.showOnDate).endOf('month').toDate();

        const getDaysCount = (end?: Date): number =>
          !!end ? moment(end).diff(moment(month.showOnDate), 'days') + 1 : 1;

        if (lastMonthInView === month.id) {
          const rangeEndDate: Date | undefined = !!showOutOfRange
            ? lastInView
            : first(sortBy([lastInView, endDate, monthEndDate]));

          return {
            ...month,
            daysCount: getDaysCount(rangeEndDate),
          };
        }

        return {
          ...month,
          daysCount: getDaysCount(monthEndDate),
        };
      });

    return {
      datesInView,
      daysInView,
      monthsInView,
    };
  }, [numberOfDaysInView, viewStartDate, endDate, showOutOfRange]);

  const calendarRange = useMemo(() => {
    return {
      startDate: resolveCalendarRangeCapDate(startDate),
      endDate: handleCalendarRangeEndDST(resolveCalendarRangeCapDate(endDate)),
    };
  }, [startDate, endDate]);

  const navigationEnabled =
    useMemo((): EmployeeCalendarNavigation['enabled'] => {
      const inView = {
        start: first(datesInView)?.date,
        end: last(datesInView)?.date,
      };

      const currentViewIsOnRange = {
        start: !!inView.start
          ? moment(inView.start).isSameOrBefore(
              moment(calendarRange.startDate),
              'day',
            )
          : false,
        end: !!inView.end
          ? moment(inView.end).isSameOrAfter(
              moment(calendarRange.endDate),
              'day',
            )
          : false,
      };

      return {
        previous: !currentViewIsOnRange.start,
        next: !currentViewIsOnRange.end,
      };
    }, [datesInView, calendarRange]);

  return {
    viewStartDate,
    daysInView,
    datesInView,
    monthsInView,
    navigation: {
      enabled: navigationEnabled,
      onNavigate: handleCalendarNavigation,
    },
    containerRef,
    calendarRange,
  };
};
